import { useChatStore } from '@/store/useChatStore';
import { useDocumentStore } from '@/store/useDocumentStore';
import { ChatMode } from '@/utils/constants';
import { createDeepgramLiveListen } from '@/utils/deepgram-config';
import { LiveTranscriptionEvents } from '@deepgram/sdk';
import { Icon } from '@iconify/react';
import { Button, ScrollShadow } from '@nextui-org/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AudioModeInput } from './audio-mode-input';
import { TextModeInput } from './text-mode-input';

const KEEP_ALIVE_INTERVAL = 5000; // 5 seconds

interface InputBoxProps {
  /** Returns false when the message was refused, so the text is kept. */
  onSendMessage: (message: string) => boolean | void;
  isThinking: boolean;
  isSpeaking: boolean;
  stopSpeaking: () => void;
  isInitiatedSpeaking: boolean;
  setIsSettingsOpen: (isOpen: boolean) => void;
  onSaveToMemory: () => void;
  isSavingMemory?: boolean;
}

function InputBox({
  onSendMessage,
  isSpeaking,
  stopSpeaking,
  isInitiatedSpeaking,
  setIsSettingsOpen,
  onSaveToMemory,
  isSavingMemory,
}: InputBoxProps) {
  const { isThinking, showSendButton, chatMode, setChatMode, selectedPrompt } =
    useChatStore();

  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const microphoneRef = useRef<MediaRecorder | null>(null);
  const [isLoading, setLoading] = useState(false);

  const socketRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentInputTextRef = useRef('');
  const committedTranscriptRef = useRef('');
  const pendingUtteranceRef = useRef('');
  const utteranceSegmentsRef = useRef<string[]>([]);
  const lastFinalTranscriptRef = useRef('');

  const syncTranscriptDisplay = useCallback(() => {
    const display = [
      committedTranscriptRef.current.trim(),
      utteranceSegmentsRef.current.join(' ').trim(),
      pendingUtteranceRef.current.trim(),
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    currentInputTextRef.current = display;
    setInputText(display);
  }, []);

  const finalizeCurrentUtterance = useCallback(() => {
    const utterance = utteranceSegmentsRef.current.join(' ').trim();
    const pending = pendingUtteranceRef.current.trim();
    const toCommit = utterance || pending;

    utteranceSegmentsRef.current = [];
    pendingUtteranceRef.current = '';
    lastFinalTranscriptRef.current = '';

    if (!toCommit) {
      syncTranscriptDisplay();
      return;
    }

    const committed = committedTranscriptRef.current.trim();
    committedTranscriptRef.current = committed
      ? `${committed} ${toCommit}`.trim()
      : toCommit;

    syncTranscriptDisplay();
  }, [syncTranscriptDisplay]);

  const handleTranscriptEvent = useCallback(
    (data: any) => {
      const receivedTranscript = data.channel.alternatives[0]?.transcript?.trim();
      if (!receivedTranscript) return;

      if (data.is_final) {
        if (receivedTranscript !== lastFinalTranscriptRef.current) {
          const segments = utteranceSegmentsRef.current;
          const lastSegment = segments[segments.length - 1]?.trim() || '';

          if (lastSegment && receivedTranscript.startsWith(lastSegment)) {
            segments[segments.length - 1] = receivedTranscript;
          } else if (receivedTranscript !== lastSegment) {
            segments.push(receivedTranscript);
          }

          lastFinalTranscriptRef.current = receivedTranscript;
        }

        pendingUtteranceRef.current = '';
        syncTranscriptDisplay();

        if (data.speech_final) {
          finalizeCurrentUtterance();
        }

        return;
      }

      pendingUtteranceRef.current = receivedTranscript;
      syncTranscriptDisplay();
    },
    [finalizeCurrentUtterance, syncTranscriptDisplay]
  );

  const resetTranscriptRefs = useCallback(() => {
    currentInputTextRef.current = '';
    committedTranscriptRef.current = '';
    pendingUtteranceRef.current = '';
    utteranceSegmentsRef.current = [];
    lastFinalTranscriptRef.current = '';
  }, []);

  const clearInput = useCallback(() => {
    setInputText('');
    resetTranscriptRefs();
  }, [resetTranscriptRefs]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputText(value);

      const trimmed = value.trim();
      if (!trimmed) {
        resetTranscriptRefs();
        return;
      }

      committedTranscriptRef.current = trimmed;
      currentInputTextRef.current = trimmed;
      pendingUtteranceRef.current = '';
      utteranceSegmentsRef.current = [];
      lastFinalTranscriptRef.current = '';
    },
    [resetTranscriptRefs]
  );

  const { selectedFiles, clearSelectedFile } = useDocumentStore();

  const toggleListening = useCallback(async () => {
    if (isListening) {
      await stopListening();
    } else {
      await startListening();
    }
  }, [isListening]);

  const stopListeningForEdit = useCallback(async () => {
    if (isListening) {
      await stopListening();
    }
  }, [isListening]);

  const startKeepAlive = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }

    keepAliveIntervalRef.current = setInterval(() => {
      if (socketRef.current) {
        socketRef.current.send(JSON.stringify({ type: 'KeepAlive' }));
      }
    }, KEEP_ALIVE_INTERVAL);
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const startListening = async () => {
    setLoading(true);
    pendingUtteranceRef.current = '';
    utteranceSegmentsRef.current = [];
    lastFinalTranscriptRef.current = '';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      microphoneRef.current = new MediaRecorder(stream);
      streamRef.current = stream;

      socketRef.current = await createDeepgramLiveListen({
        language: 'en-US',
        model: 'nova-2',
        ...(showSendButton && {
          smart_format: true,
          interim_results: true,
          utterance_end_ms: 1000,
          endpointing: 500,
        }),
        ...(!showSendButton && {
          interim_results: true,
          endpointing: 1000,
          utterance_end_ms: 2000,
          punctuate: true,
        }),
      });

      socketRef.current.addListener(
        LiveTranscriptionEvents.Error,
        (error: unknown) => {
        console.error('Deepgram connection error:', error);
        setLoading(false);
        void stopListening();
        toast.error('Speech-to-text connection failed.');
        }
      );

      socketRef.current.addListener(LiveTranscriptionEvents.Close, () => {
        setLoading(false);
        setIsListening(false);
      });

      socketRef.current.addListener(LiveTranscriptionEvents.Open, () => {
        if (microphoneRef.current) {
          if (microphoneRef.current.state === 'inactive')
            microphoneRef.current.start(250);
          else {
            stopListening();
            startListening();
            // microphoneRef.current.start(250);
          }
        }
        setIsListening(true);
        setLoading(false);
        startKeepAlive(); // Start keepAlive when connection opens
      });

      socketRef.current.addListener(
        LiveTranscriptionEvents.Transcript,
        handleTranscriptEvent
      );

      socketRef.current.addListener(
        LiveTranscriptionEvents.UtteranceEnd,
        () => {
          if (chatMode === ChatMode.VOICE) {
            finalizeCurrentUtterance();

            if (currentInputTextRef.current.trim()) {
              onSendMessage(currentInputTextRef.current.trim());
            }

            resetTranscriptRefs();
            setInputText('');
            return;
          }

          finalizeCurrentUtterance();
        }
      );

      microphoneRef.current.ondataavailable = (event) => {
        if (event.data.size > 0 && socketRef.current) {
          socketRef.current.send(event.data);
        }
      };
    } catch (error) {
      setLoading(false);
      stopListening();
      if (
        (error as Error).message.includes('Deepgram access token') ||
        (error as Error).message.includes('grant')
      ) {
        toast.error('Speech-to-text is not configured.');
      } else if ((error as DOMException).name === 'NotAllowedError') {
        toast.error('Permission to access microphone is denied.');
      } else {
        console.error('Error starting recording:', error);
      }
    }
  };

  const stopListening = async () => {
    finalizeCurrentUtterance();
    stopKeepAlive();
    // setInputText("");
    if (microphoneRef.current) {
      microphoneRef.current.stop();
      microphoneRef.current.ondataavailable = null;
    }
    if (socketRef.current) {
      socketRef.current.finish();
      socketRef.current.removeAllListeners();
    }

    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      } catch (err) {
        console.error('Error stopping stream:', err);
      }
      streamRef.current = null;
    }
    setIsListening(false);

    microphoneRef.current = null;
    socketRef.current = null;
  };

  useEffect(() => {
    if (isThinking || isSpeaking) {
      stopListening();
    }
  }, [isThinking, isSpeaking]);

  useEffect(() => {
    if (chatMode !== ChatMode.VOICE) {
      stopListening();
      setLoading(false);
      return;
    }
    if (!isThinking && !isSpeaking && !isInitiatedSpeaking) {
      startListening();
    }
  }, [isThinking, isSpeaking, chatMode, isInitiatedSpeaking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopKeepAlive();
      stopListening();
      stopSpeaking();
      setIsListening(false);
      setLoading(false);
      resetTranscriptRefs();
      setInputText('');
    };
  }, [resetTranscriptRefs]);

  useEffect(() => {
    if (chatMode === ChatMode.VOICE) {
      stopListening();
      startListening();
    }
  }, [showSendButton]);

  const renderSelectedFiles = () => {
    if (!selectedFiles?.length) return null;

    return (
      <ScrollShadow className="max-h-[100px]">
        <div className="px-4 py-2 flex gap-2 flex-wrap">
          {Array.from(selectedFiles).map((file: File, index: number) => (
            <div
              key={index}
              className="flex items-center gap-2 py-1 px-2  border-1 rounded-lg"
            >
              <Icon
                icon="mdi:file-document-outline"
                className="h-5 w-5 text-neutral-500 flex-shrink-0"
              />
              <span className="text-sm truncate flex-grow">
                {(file as any).filename}
              </span>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => clearSelectedFile((file as any).id)}
              >
                <Icon icon="mdi:close" className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollShadow>
    );
  };

  return (
    <div className="relative w-full">
      {chatMode === ChatMode.VOICE && selectedPrompt?.enable_audio ? (
        <AudioModeInput
          isListening={isListening}
          isLoading={isLoading}
          inputText={inputText}
          onToggleListening={toggleListening}
          onSendMessage={(text) => {
            onSendMessage(text);
            clearInput();
          }}
          onClearInput={clearInput}
          stopSpeaking={stopSpeaking}
        />
      ) : (
        <>
          <TextModeInput
            inputText={inputText}
            isListening={isListening}
            isLoading={isLoading}
            isSubmitting={isThinking}
            onInputChange={handleInputChange}
            onSendMessage={async (text) => {
              if (isListening) {
                await stopListening();
                text = currentInputTextRef.current.trim() || text;
              }
              if (text && onSendMessage(text) !== false) {
                clearInput();
              }
            }}
            onToggleListening={toggleListening}
            onStopListeningForEdit={stopListeningForEdit}
            setIsSettingsOpen={setIsSettingsOpen}
            setChatMode={setChatMode}
            onSaveToMemory={onSaveToMemory}
            isSavingMemory={isSavingMemory}
          />
          {renderSelectedFiles()}
        </>
      )}
    </div>
  );
}

export default InputBox;
